import { LitElement, html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { classMap } from 'lit-html/directives/class-map.js';

import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';

import { isDealerPage, logErrorToSentry } from '@Helpers/helpers';
import {
  analyticsCtaDataObject,
  handleAdobeNavEvent,
} from '../../helpers/adobe-analytics';

import { styles } from './css';

class GlobalFooter extends LitElement {
  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      styles,
    ];
  }

  static properies = {
    addTopMargin: { type: Boolean },
  };

  constructor() {
    super();
    this.addTopMargin = !document.getElementsByTagName('ralph-block').length;
  }

  async connectedCallback() {
    super.connectedCallback();
    try {
      // https://help.nice-incontact.com/content/acd/digital/chat/customizechatchannelsusingapi.htm
      await this.ringCentralChatInit(
        window,
        document,
        'script',
        'brandembassy',
        'https://livechat-static-de-na1.niceincontact.com/4/chat.js'
      );
    } catch (error) {
      logErrorToSentry(error);
    }

    if (isDealerPage()) {
      this.sendAnalyticsDealerPageEvent();
    }
  }

  render() {
    return html`
      <footer
        style="${styleMap(this.backgroundColor)}"
        class="block-grid footer-layout"
        class="${classMap({
          'block-grid': true,
          'footer-layout': true,
          'add-top-margin': this.addTopMargin,
        })}"
      >
        <div class="main">
          <div class="header">
            <h1>${unsafeHTML(this.withLineBreaks(this.mainData.header))}</h1>
            <cta-text
              cta-data=${this.primaryCtaData}
              .ctaAnalyticsData=${analyticsCtaDataObject(
                this.primaryCtaData,
                `Global Footer`
              )}
            ></cta-text>
          </div>
          <ul class="socials">
            ${this.mainData.socials.map(social => {
              return html`
                <li>
                  <a
                    @click="${event => {
                      handleAdobeNavEvent({
                        navString: social.navString,
                        destinationUrl: social.href,
                        target_blank: true,
                        optional_rel: 'noopener',
                        event,
                      });
                    }}"
                    tabindex="1"
                    href="${social.href}"
                  >
                    <img src=${social.svgSrc} class="footer-icon" />
                  </a>
                </li>
              `;
            })}
          </ul>
        </div>
        <div class="drivers">
          ${this.driversData.map(driverSection => {
            return html`
              <div class="driver-section">
                <p class="section-header">${driverSection.category}</p>
                ${driverSection.links.map(link => {
                  if (isDealerPage()) {
                    if (link.division && link.division === '3form')
                      return html``;
                  } else {
                    if (link.division && link.division === 'elements')
                      return html``;
                  }
                  return html`
                    <a
                      href=${ifDefined(link.href ? link.href : undefined)}
                      tabindex="1"
                      data-name=${link.text}
                      @click=${event => {
                        event.preventDefault();
                        handleAdobeNavEvent({
                          navString: link.navString,
                          destinationUrl: link.href,
                          event,
                          target: `${link.target ? link.target : '_self'}`,
                        });
                      }}
                    >
                      ${link.svgSrc
                        ? html`
                            <img
                              src=${link.svgSrc}
                              class="footer-icon driver-icon"
                            />
                          `
                        : ``}
                      ${link.text}
                    </a>
                  `;
                })}
              </div>
            `;
          })}
        </div>
        <div class="terms">
          <ul class="left">
            <a><li>&copy; ${new Date().getFullYear()} 3form, LLC.</li></a>
          </ul>
          <ul class="right">
            ${this.termsData.right.map(term => {
              return html`
                <li>
                  <a
                    @click=${event => {
                      event.preventDefault();
                      handleAdobeNavEvent({
                        navString: term.navString,
                        destinationUrl: term.href,
                        event,
                        target: `${term.target ? term.target : '_self'}`,
                      });
                    }}
                    href="${term.link}"
                  >
                    ${term.text}
                  </a>
                </li>
              `;
            })}
            <li>
              <p class="opt-out" @click="${this.tryOpenOptOut}">
                Do Not Sell or Share My Personal Information
              </p>
            </li>
          </ul>
        </div>
      </footer>
    `;
  }

  tryOpenChat(e) {
    if (
      e.target.getAttribute('data-name') === 'Chat' &&
      typeof brandembassy === 'function'
    ) {
      brandembassy('openChatWindow');
    }
  }

  // intialize chat https://help.nice-incontact.com/content/acd/digital/chat/customizechatchannelsusingapi.htm
  ringCentralChatInit(i, s, o, r, g, v, a, m) {
    g = v ? g + '?v=' + v : g;
    i['BrandEmbassy'] = r;
    i[r] =
      i[r] ||
      function() {
        (i[r].q = i[r].q || []).push(arguments);
      };
    i[r].l = +new Date();
    a = s.createElement(o);
    m = s.getElementsByTagName(o)[0];
    a.async = 1;
    a.src = g + '?' + Math.round(Date.now() / 1000 / 3600);
    m.parentNode.insertBefore(a, m);

    if (typeof brandembassy === 'function') {
      brandembassy('init', 3620, 'chat_2a09d20e-b9b8-4401-9477-9d05171ef4be');
    }
  }

  withLineBreaks(string) {
    return !string ? '' : string.split(/\r\n|\n/g).join('<br>');
  }

  tryOpenOptOut() {
    const el = document.getElementById('SeersDoNotSell');
    if (el) el.click();
  }

  sendAnalyticsDealerPageEvent() {
    if (typeof gtag !== 'function') return;

    gtag('event', 'Elements Page', {
      event_category: 'Elements',
    });
  }

  get backgroundColor() {
    return {
      'background-color': isDealerPage() ? '#305371' : '#1F1F1F',
    };
  }

  get mainData() {
    return this.footerData.main;
  }

  get driversData() {
    return this.footerData.drivers;
  }

  get termsData() {
    return this.footerData.terms;
  }

  get primaryCtaData() {
    return JSON.stringify(this.mainData.primaryCta);
  }

  get footerData() {
    return {
      main: {
        header: 'Stay Stoked and in the Loop\nwith our Latest Innovations',
        primaryCta: {
          text: 'Sign up for our newsletter',
          url: 'https://mailchi.mp/3-form.com/newsletter',
          useWhite: true,
          notBold: true,
        },
        socials: [
          {
            text: 'Facebook',
            svgSrc: '/static/icons/SVG/facebook.svg',
            href: '//www.facebook.com/3formMaterialSolutions',
            class: 'icon-facebook',
            navString: 'EXIT|Global Footer|Facebook',
          },
          {
            text: 'Youtube',
            svgSrc: '/static/icons/SVG/youtube.svg',
            href: '//www.youtube.com/user/3formVideo',
            class: 'icon-youtube',
            navString: 'EXIT|Global Footer|Youtube',
          },
          {
            text: 'Pinterest',
            svgSrc: '/static/icons/SVG/pinterest.svg',
            href: '//www.pinterest.com/3formusa/',
            class: 'icon-pinterest',
            navString: 'EXIT|Global Footer|Pinterest',
          },
          {
            text: 'Instagram',
            svgSrc: '/static/icons/SVG/instagram.svg',
            href: '//instagram.com/3form/',
            class: 'icon-instagram',
            navString: 'EXIT|Global Footer|Instagram',
          },
        ],
      },
      drivers: [
        {
          category: 'Resources',
          links: [
            {
              text: 'Tech Specs & Downloads',
              href: '/tech-specs',
              navString: 'NAV|Global Footer|Tech Specs',
            },
            {
              text: 'CEUs',
              href: isDealerPage() ? '/elements/ceus' : '/ceus',
              navString: 'NAV|Global Footer|CEUs',
            },
            {
              text: 'Hardware Catalog',
              href: 'https://cart.3-form.com/t/hardware-catalog',
              navString: 'NAV|Global Footer|Hardware Catalog',
            },
            {
              text: 'Hardware Systems',
              href: '/hardware',
              navString: 'NAV|Global Footer|Hardware Systems',
            },
            {
              text: 'My3form',
              href: '/my3form.php/mode/login',
              navString: 'NAV|Global Footer|My3form',
            },
            {
              text: 'Find an Order',
              href: '/find-an-order',
              navString: 'NAV|Global Footer|Find an Order',
            },
          ],
        },
        {
          category: 'Orders',
          links: [
            {
              text: '3form Direct',
              href: '/direct',
              navString: 'NAV|Global Footer|Direct',
            },
            {
              text: 'Reclaim',
              href: 'https://3form-reclaim.myshopify.com/',
              target: '_blank',
              navString: 'EXIT|Global Footer|Reclaim',
            },
            {
              text: 'Ordering FAQs',
              href: '/order-faq',
              navString: 'NAV|Global Footer|Order FAQ',
            },
            {
              text: 'Conditions of Sale',
              href: '/conditions-of-sale',
              navString: 'NAV|Global Footer|Conditions of Sale',
            },
          ],
        },
        {
          category: 'Contact',
          links: [
            {
              svgSrc: '/static/icons/SVG/chat.svg',
              text: 'Chat',
              href: '',
              navString: 'INP|Global Footer|Open Chat',
            },
            {
              division: '3form',
              svgSrc: '/static/icons/SVG/telephone.svg',
              text: '1 800 726 0126',
              href: 'tel:18007260126',
              navString: 'INP|Global Footer|3form Phone',
            },
            {
              division: '3form',
              svgSrc: '/static/icons/SVG/email.svg',
              text: 'info@3-form.com',
              href: 'mailto:info@3-form.com',
              navString: 'INP|Global Footer|Email',
            },
            {
              division: 'elements',
              svgSrc: '/static/icons/SVG/telephone.svg',
              text: '801-649-2522',
              href: 'tel:18016492522',
              navString: 'INP|Global Footer|Elements Phone',
            },
            {
              division: 'elements',
              svgSrc: '/static/icons/SVG/email.svg',
              text: 'elements@3-form.com',
              href: 'mailto:elements@3-form.com',
              navString: 'INP|Global Footer|Elements Email',
            },
            {
              svgSrc: '/static/icons/SVG/pin.svg',
              text: 'Locations',
              href: '/locations',
              navString: 'NAV|Global Footer|Locations',
            },
          ],
        },
      ],
      terms: {
        right: [
          {
            text: 'Careers',
            link: '/careers',
            navString: 'NAV|Global Footer|Careers',
          },
          {
            text: 'IP Notices',
            link: '/ip',
            navString: 'NAV|Global Footer|IP',
          },
          {
            text: 'Privacy',
            link: '/privacy',
            navString: 'NAV|Global Footer|Privacy',
          },
          {
            text: 'Forced Labor Policy',
            link: '/tech-specs/1705',
            target: '_blank',
            navString: 'NAV|Global Footer|Forced Labor Policy',
          },
          {
            text: 'Terms & Conditions',
            link: '/terms',
            navString: 'NAV|Global Footer|Terms',
          },
        ],
      },
    };
  }
}

customElements.define('global-footer', GlobalFooter);
